import { useFrame } from "@react-three/fiber";
import { useRef } from "react";
import type { Group } from "three";
import { useMediaPreferences } from "../hooks/useMediaPreferences";
import { LabelSprite } from "./LabelSprite";
import { useSunlightArrows } from "./useSunlightArrows";

const RAY_ORIGINS = [
  [-16, -4.2, -2.5],
  [-16, -2.2, 2.9],
  [-16, 0, -4],
  [-16, 0.4, 4.2],
  [-16, 2.5, -2.7],
  [-16, 4.3, 1.7],
] as const;

const RAY_LENGTH = 30;
const PULSES_PER_RAY = 2;
const PULSE_SPEED_UNITS_PER_SECOND = 4.5;

function pulseDistance(
  rayIndex: number,
  pulseIndex: number,
  elapsedSeconds: number,
): number {
  const phase = (rayIndex * 0.31 + pulseIndex / PULSES_PER_RAY) % 1;
  const cycle =
    ((elapsedSeconds * PULSE_SPEED_UNITS_PER_SECOND) / RAY_LENGTH + phase) % 1;
  return cycle * RAY_LENGTH;
}

function SunlightPulses() {
  const { prefersReducedMotion } = useMediaPreferences();
  const group = useRef<Group>(null);

  useFrame(({ clock }) => {
    if (prefersReducedMotion || group.current === null) {
      return;
    }

    group.current.children.forEach((pulse, index) => {
      const rayIndex = Math.floor(index / PULSES_PER_RAY);
      const origin = RAY_ORIGINS[rayIndex];
      if (origin === undefined) {
        return;
      }
      pulse.position.set(
        origin[0] +
          pulseDistance(rayIndex, index % PULSES_PER_RAY, clock.elapsedTime),
        origin[1],
        origin[2],
      );
    });
  });

  return (
    <group ref={group}>
      {RAY_ORIGINS.flatMap((origin, rayIndex) =>
        Array.from({ length: PULSES_PER_RAY }, (_, pulseIndex) => (
          <mesh
            key={`${origin.join(",")}-${pulseIndex}`}
            position={[
              origin[0] + pulseDistance(rayIndex, pulseIndex, 0),
              origin[1],
              origin[2],
            ]}
          >
            <sphereGeometry args={[0.1, 12, 8]} />
            <meshBasicMaterial
              color="#ffe9a8"
              depthWrite={false}
              opacity={0.9}
              toneMapped={false}
              transparent
            />
          </mesh>
        )),
      )}
    </group>
  );
}

export function SunlightRays() {
  const arrows = useSunlightArrows(RAY_ORIGINS);

  return (
    <group>
      {arrows.map((arrow, index) => (
        <primitive key={RAY_ORIGINS[index]?.join(",")} object={arrow} />
      ))}
      <SunlightPulses />
      <LabelSprite
        text="Sunlight"
        position={[-11.5, 5.2, 0]}
        scale={[3.2, 0.8, 1]}
        color="#ffd166"
      />
    </group>
  );
}
